import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { PIPELINE_STAGES, currency, fmtDate, fmtDateTime, countryFlag, STAGE_BY_ID } from "@/lib/format";
import { ArrowLeft, FileText, Upload, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function LeadDetail({ role }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const [l, t, h] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/leads/${id}/timeline`),
        api.get(`/hospitals`),
      ]);
      setLead(l.data); setTimeline(t.data); setHospitals(h.data);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const updateLead = async (changes) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, changes);
      setLead(data);
      const tl = await api.get(`/leads/${id}/timeline`); setTimeline(tl.data);
      toast.success("Updated");
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const payload = { lead_id: id, title: "Note added", description: note, event_type: "note" };
      // timeline endpoint requires patient_id; use direct insert via lead timeline...
      // We don't have a generic lead-timeline POST; reusing patient endpoint requires patient_id.
      // Workaround: hit the lead PATCH with appended notes
      await api.patch(`/leads/${id}`, { notes: `${lead.notes || ""}\n[${new Date().toLocaleString()}] ${note}`.trim() });
      setNote(""); load(); toast.success("Note added");
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  if (!lead) return <div className="text-sm text-[#5C6660]">Loading…</div>;

  const stage = STAGE_BY_ID[lead.stage];
  const stageIdx = PIPELINE_STAGES.findIndex((s) => s.id === lead.stage);

  return (
    <div data-testid="lead-detail">
      <Link to={`/${role}/leads`} className="inline-flex items-center gap-1.5 text-xs text-[#5C6660] hover:text-[#2A4D3E] mb-4">
        <ArrowLeft size={14} /> Back to leads
      </Link>
      <PageHeader
        eyebrow={`${countryFlag(lead.country)} ${lead.country || "Unknown origin"} · ${lead.source}`}
        title={lead.patient_name}
        subtitle={`${lead.treatment} · Estimated ${currency(lead.estimated_value)}`}
        actions={
          <Link to={`/${role}/quotations/new?lead_id=${lead.id}`} className="bg-[#D97757] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#C26243] flex items-center gap-2" data-testid="create-quote-button">
            <FileText size={15} /> Create quotation
          </Link>
        }
      />

      {/* Stage progression */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-1">Current stage</div>
            <StatusBadge kind="stage" value={lead.stage} />
          </div>
          <div className="text-xs text-[#8E9992]">{stageIdx + 1} of {PIPELINE_STAGES.length}</div>
        </div>
        <div className="h-1.5 bg-[#F2F4F3] rounded-full overflow-hidden mb-3">
          <div className="h-full bg-[#2A4D3E] transition-all" style={{ width: `${((stageIdx + 1) / PIPELINE_STAGES.length) * 100}%` }} />
        </div>
        <div className="flex flex-wrap gap-2">
          {PIPELINE_STAGES.map((s) => (
            <button key={s.id} onClick={() => updateLead({ stage: s.id })}
              data-testid={`stage-pill-${s.id}`}
              style={{ borderColor: s.id === lead.stage ? s.color : "#E8E6E1", color: s.id === lead.stage ? s.color : "#5C6660", background: s.id === lead.stage ? s.bg : "#fff" }}
              className="text-[11px] px-3 py-1.5 rounded-full border font-medium hover:border-[#2A4D3E] hover:text-[#2A4D3E] transition">
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-4 text-[#2A332C]">Lead details</div>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Email" value={lead.email || "—"} />
              <Info label="Phone" value={lead.phone || "—"} />
              <Info label="Treatment" value={lead.treatment} />
              <Info label="Source" value={lead.source} />
              <Info label="Estimated value" value={currency(lead.estimated_value)} />
              <Info label="Preferred hospital" value={hospitals.find((h) => h.id === lead.preferred_hospital_id)?.name || "—"} />
              <Info label="Created" value={fmtDateTime(lead.created_at)} />
              <Info label="Last updated" value={fmtDateTime(lead.updated_at)} />
            </dl>
          </div>

          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-3 text-[#2A332C]">Notes</div>
            <div className="text-sm text-[#5C6660] whitespace-pre-wrap mb-4 min-h-[40px]">{lead.notes || "No notes yet."}</div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
                data-testid="lead-note-input"
                className="flex-1 border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-sm focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
              <button onClick={addNote} data-testid="add-note-button"
                className="bg-[#2A4D3E] text-white rounded-full px-5 text-sm font-medium hover:bg-[#1E3B2F] flex items-center gap-1.5">
                <MessageSquare size={14} /> Add
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 h-fit" data-testid="lead-timeline">
          <div className="font-heading font-semibold text-lg mb-5 text-[#2A332C]">Timeline</div>
          <div className="relative border-l-2 border-[#E8E6E1] ml-2 space-y-6">
            {timeline.map((t) => (
              <div key={t.id} className="relative pl-6">
                <div className="absolute w-3 h-3 rounded-full bg-[#2A4D3E] -left-[7px] top-1 border-2 border-white" />
                <div className="text-sm font-medium text-[#2A332C]">{t.title}</div>
                {t.description && <div className="text-xs text-[#5C6660] mt-0.5">{t.description}</div>}
                <div className="text-[10px] text-[#8E9992] mt-1.5">{fmtDateTime(t.created_at)} · {t.actor_name || "System"}</div>
              </div>
            ))}
            {timeline.length === 0 && <div className="text-sm text-[#8E9992] pl-4">No timeline events yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold mb-0.5">{label}</div>
      <div className="text-sm text-[#2A332C]">{value}</div>
    </div>
  );
}
